import type { Announcement } from '@zennolab_platform/data-access-notification-service-api';
import { Container, Service } from 'typedi';

import type {
  IAnnouncementEntity,
  IDraftFields,
  IEditPublishedFields,
  IVersionCardFields,
} from '../types/announcement';
import { toAnnouncementEntity } from './mappers/announcement';
import { ANNOUNCEMENT_API } from './types';

const entity = ({ announcement }: { announcement?: Announcement }): IAnnouncementEntity => {
  if (!announcement) throw new Error('The service returned no announcement');
  return toAnnouncementEntity(announcement);
};

/** AnnouncementAdminService of notificationservice over gRPC-Web. */
@Service()
export class AnnouncementService {
  private readonly _client = Container.get(ANNOUNCEMENT_API);

  list = async (): Promise<IAnnouncementEntity[]> =>
    (await this._client.list({})).announcements.map(toAnnouncementEntity);

  get = async (id: string): Promise<IAnnouncementEntity> => entity(await this._client.get({ id }));

  createDraft = async (fields: IDraftFields): Promise<IAnnouncementEntity> =>
    entity(await this._client.createDraft(fields));

  updateDraft = async (fields: IDraftFields & { id: string }): Promise<IAnnouncementEntity> =>
    entity(await this._client.updateDraft(fields));

  publish = async (id: string): Promise<IAnnouncementEntity> => entity(await this._client.publish({ id }));

  schedule = async (id: string, publishAt: Date | undefined): Promise<IAnnouncementEntity> =>
    entity(await this._client.schedule({ id, publishAt }));

  withdraw = async (id: string): Promise<IAnnouncementEntity> => entity(await this._client.withdraw({ id }));

  editPublished = async (fields: IEditPublishedFields): Promise<IAnnouncementEntity> =>
    entity(await this._client.editPublished(fields));

  upsertVersionCard = async (fields: IVersionCardFields): Promise<IAnnouncementEntity> =>
    entity(await this._client.upsertVersionCard(fields));
}
